import '../css/app.css';

import { createInertiaApp, Head, Link } from '@inertiajs/vue3';
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers';
import { createApp, h } from 'vue';
import { ZiggyVue } from 'ziggy-js';
import AppLayout from '@/layouts/AppLayout.vue'
import ToastContainer from '@/components/ToastContainer.vue'
import ToastPlugin from '@/plugins/toast.js';
import ChangelogPopup from '@/components/ChangelogPopup.vue';
import changelogPopupPlugin from '@/plugins/changelogPopup.js';
import ConfirmActionPlugin from '@/plugins/confirmAction.js';
import { vMaska } from "maska/vue"
import axios from 'axios'
import { router } from '@inertiajs/vue3'
import { installInertiaCsrfRecovery, installAxiosCsrfRetry, startSessionKeepAlive } from '@/helpers/csrf.js';

// Configure axios defaults. CSRF needs no header setup here: axios (and
// Inertia's built-in XHR client) echo Laravel's XSRF-TOKEN cookie automatically
// (see helpers/csrf.js).
axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';

// Inertia caches each page's props in history state and restores them on
// browser back/forward (popstate) WITHOUT a server request. For edit/create
// forms that means stale data can reappear after the record was changed (a full
// F5 fixes it because it refetches). Detect a history restore of a form page and
// refetch fresh props from the server, remounting the component so useForm()
// re-seeds from the fresh data.
let restoredFromHistory = false;

// Only browser back/forward emits popstate; Inertia <Link> visits use pushState.
window.addEventListener('popstate', () => {
    restoredFromHistory = true;
});

router.on('navigate', (event) => {
    if (!restoredFromHistory) return;
    restoredFromHistory = false;

    // Convention: form pages (…/Edit, …/Create) must always be fresh.
    const component = event.detail.page.component;
    if (!/\/(Edit|Create)$/.test(component)) return;

    // NB: router.reload() cannot be used here — it hardcodes preserveState:true,
    // which keeps the component (and its stale useForm refs) mounted. We need
    // preserveState:false so the component remounts and re-seeds from fresh
    // props. replace:true keeps the history stack intact (we're mid-popstate,
    // so a normal push would clobber the forward entry).
    router.get(window.location.href, {}, {
        preserveState: false,
        preserveScroll: true,
        replace: true,
        headers: { 'Cache-Control': 'no-cache' },
    });
});

// Make axios available globally
window.axios = axios;

// CSRF: transparent 419 recovery for Inertia visits and direct axios calls,
// plus the session keep-alive. All logic lives in helpers/csrf.js.
installInertiaCsrfRecovery(router);
installAxiosCsrfRetry(axios);
startSessionKeepAlive();

const appName = import.meta.env.VITE_APP_NAME || 'WIZZR';

createInertiaApp({
    title: (title) => `${title} - ${appName}`,
    resolve: (name) => {
        return resolvePageComponent(
            `./pages/${name}.vue`,
            import.meta.glob('./pages/**/*.vue', {
                eager: false
            })
        );
    },
    setup({ el, App, props, plugin }) {
        const ziggyConfig = {
            ...props.initialPage.props.ziggy.routes,
            location: new URL(props.initialPage.props.ziggy.location),
        };

        const app = createApp({
            // ToastContainer is rendered as a persistent sibling of the Inertia
            // App so it stays mounted across page navigations. Rendering it inside
            // AppLayout (which re-mounts on every navigation) reset each toast's
            // auto-remove timer and the flash dedup map, causing toasts to
            // duplicate and never expire while clicking quickly between pages.
            render: () => [h(App, props), h(ToastContainer)],
        })
            .use(plugin)
            .use(ZiggyVue, ziggyConfig)
            .use(ToastPlugin)
            .use(changelogPopupPlugin)
            .use(ConfirmActionPlugin)
            .directive('maska', vMaska)
            .component('Link', Link)
            .component('Head', Head)
            .component('AppLayout', AppLayout)
            .component('ChangelogPopup', ChangelogPopup);

        return app.mount(el);
    },
    progress: {
        color: '#4F46E5',
        delay: 250,
        showSpinner: true,
    },
});
